// src/components/create-project/ProjectListModal.tsx
import { FC, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Project } from './types/project';
import { ProjectForm } from './ProjectForm';
import { useProjects } from './hooks/useProjects';
import { useState } from 'react';
import { Loader2, FolderPlus, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConfirmModal from '../common/ConfirmModal';
import toast from 'react-hot-toast';

interface ProjectListModalProps {
  open: boolean;
  onClose: () => void;
  onSelectProject: (project: Project) => void;
  canManageProjects?: boolean;
}

export const ProjectListModal: FC<ProjectListModalProps> = ({
  open,
  onClose,
  onSelectProject,
  canManageProjects = false,
}) => {
  const { projects, loading, fetchProjects, removeProject } = useProjects();
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open, fetchProjects]);

  const handleClose = () => {
    if (!isCreateProjectOpen) {
      onClose();
    }
  };

  const handleProjectSelect = (project: Project) => {
    onSelectProject(project);
    onClose();
    window.location.reload();
  };

  const handleProjectCreated = async (newProject: Project) => {
    setIsCreateProjectOpen(false);
    await fetchProjects();
  };

  const handleDeleteClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      await removeProject(projectToDelete.projectId);
      await fetchProjects();
      toast.success("Project deleted successfully");
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  };

  if (isCreateProjectOpen) {
    if (!canManageProjects) {
      return null;
    }
    return (
      <ProjectForm
        open={true}
        onClose={() => setIsCreateProjectOpen(false)}
        onSuccess={handleProjectCreated}
      />
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold">Select a Project</DialogTitle>
            <DialogDescription>
              Choose an existing project or create a new one to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {projects.map((project) => (
                  <div
                    key={project.projectId}
                    className="group relative rounded-xl border border-gray-200 hover:border-primary-200 hover:bg-primary-50 transition-all"
                  >
                    <div className="w-full p-4 flex items-center justify-between">
                      <button
                        onClick={() => handleProjectSelect(project)}
                        className="flex-1 flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-white">
                          <FolderPlus className="w-5 h-5 text-gray-400 group-hover:text-primary-500" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">{project.name}</h3>
                          <p className="text-sm text-gray-500">Last modified on {new Date(project.updatedAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })} at {new Date(project.updatedAt).toLocaleString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true,
                          })}</p>
                        </div>
                      </button>
                      
                      {canManageProjects && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-2 text-gray-400 hover:text-red-500"
                          onClick={(e) => handleDeleteClick(e, project)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {canManageProjects && (
                  <button
                    onClick={() => setIsCreateProjectOpen(true)}
                    className="flex items-center justify-center gap-2 p-4 rounded-xl border border-dashed border-gray-300 hover:border-primary-300 hover:bg-primary-50 transition-all"
                  >
                    <Plus className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300 font-medium">Create New Project</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {canManageProjects && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Delete Project"
          message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setProjectToDelete(null);
          }}
          confirmText="Delete"
          cancelText="Cancel"
          isProcessing={isDeleting}
        />
      )}
    </>
  );
};